/**
 * Financeiro — contas a receber, contas a pagar e o resumo do módulo.
 *
 * O contrato é `docs/plano/fluxo-de-venda.md` §2 mais o adendo
 * `docs/plano/lancamento-financeiro.md`, e o banco por trás é a migração
 * `0012_venda.sql` com as colunas novas da `0015`. Cinco pontos decidem quase
 * tudo que as telas fazem:
 *
 * 1. A identidade que atravessa a fronteira é o CÓDIGO — `R0001` para a conta
 *    a receber, `P0001` para a de pagar (CONSTITUICAO.md §2: nenhuma URL
 *    exibe uuid). O `numero_documento` (`REC-000123`) é outra coisa: é o
 *    número que o lojista escolhe e imprime, editável, e não endereça rota.
 *
 * 2. `atrasada` NÃO é status: no banco existem `aberta`, `recebida`/`paga` e
 *    `cancelada`; atrasada é `aberta` com `vencimento < hoje`, derivada na
 *    consulta. Uma coluna de status que envelhece sozinha mente no dia
 *    seguinte — por isso a tela também deriva o destaque de `vencimento`
 *    (`urgenciaDaConta`) em vez de confiar num rótulo gravado.
 *
 * 3. Os dois lados — receber e pagar — têm a MESMA forma de listagem, de
 *    filtro, de contagem e de soma. É de propósito: são a mesma tela com
 *    outro rótulo, e duplicar o tipo é como um lado ganha paginação e o outro
 *    não. O que muda é o nome do parâmetro de pessoa (`cliente` × `fornecedor`)
 *    e o nome do status baixado (`recebida` × `paga`), e as duas diferenças
 *    estão resolvidas aqui dentro, não nas telas.
 *
 * 4. Dinheiro é INTEIRO em centavos, ponta a ponta (CONSTITUICAO.md §1). O
 *    front SOMA e SUBTRAI inteiros — é assim que o valor líquido é calculado
 *    na tela ("bruto − desconto + acréscimo. É conta, não campo"). O que §1
 *    proíbe é DIVIDIR fora de `dinheiro.Ratear`, e por isso o rateio das
 *    parcelas continua sendo do servidor.
 *
 * 5. Título gerado por documento é IMUTÁVEL. Veio de venda ou de nota fiscal,
 *    não se edita nem se exclui — corrige-se o documento. Título manual é
 *    editável e excluível enquanto não produziu efeito. Quem responde isso é o
 *    SERVIDOR, em `editavel`, `excluivel` e `motivo_bloqueio`: o front LÊ os
 *    três e não recalcula nada. Regra duplicada é regra que diverge, e o
 *    sintoma seria um botão habilitado que falha depois do clique.
 */
import { api } from './api'
import type { Centavos } from './dinheiro'

/**
 * O período das listagens é a MESMA borda das notas fiscais — `de` inclusivo,
 * `ate` exclusivo. Reexportado em vez de reescrito: duas implementações da
 * mesma borda é ter uma delas errada na virada do mês.
 */
export { intervaloDoMes, mesesRecentes } from './notasfiscais'

/** Qual das duas contas. Decide a rota, o rótulo e o nome do parâmetro. */
export type Lado = 'receber' | 'pagar'

/**
 * Como o formulário chama a mesma coisa. "Receita — vou receber" e "Despesa —
 * vou pagar" são o vocabulário do lojista; `Lado` é o da API.
 */
export type TipoLancamento = 'receita' | 'despesa'

export function ladoDoTipo(tipo: TipoLancamento): Lado {
  return tipo === 'receita' ? 'receber' : 'pagar'
}

export function tipoDoLado(lado: Lado): TipoLancamento {
  return lado === 'receber' ? 'receita' : 'despesa'
}

/** Status gravado no banco. `recebida` só existe no receber, `paga` no pagar. */
export type StatusConta = 'aberta' | 'recebida' | 'paga' | 'cancelada'

/**
 * O que o filtro de situação aceita — o status mais `atrasada`, que é
 * derivada. Vai na query como `?situacao=`.
 */
export type SituacaoConta = StatusConta | 'atrasada'

/**
 * O KPI clicado. Vai na query como `?recorte=` e recorta a tabela inteira, não
 * a página.
 *
 * Existe porque um número que não muda decisão nenhuma não merece a dobra da
 * tela: "atrasadas" e "vence hoje" são exatamente o trabalho de hoje.
 */
export type RecorteDeContas = 'atrasadas' | 'vence_hoje' | 'vence_na_semana' | 'baixadas_no_mes'

/** Forma de pagamento prevista para o título. */
export type FormaConta = 'dinheiro' | 'pix' | 'debito' | 'credito' | 'crediario' | 'boleto'

/** O rótulo de cada forma, para o select do formulário. */
export const FORMAS: { valor: FormaConta; rotulo: string }[] = [
  { valor: 'dinheiro', rotulo: 'Dinheiro' },
  { valor: 'pix', rotulo: 'PIX' },
  { valor: 'debito', rotulo: 'Cartão de débito' },
  { valor: 'credito', rotulo: 'Cartão de crédito' },
  { valor: 'crediario', rotulo: 'Crediário da loja' },
  { valor: 'boleto', rotulo: 'Boleto' },
]

export type PessoaResumo = {
  codigo: string
  nome: string
}

/** Categoria financeira — `GET /financeiro/categorias?tipo=`. */
export type CategoriaFinanceira = {
  codigo: string
  nome: string
  tipo: TipoLancamento
}

/** Conta de caixa ou banco — `GET /financeiro/contas-financeiras`. */
export type ContaFinanceira = {
  codigo: string
  nome: string
  tipo: 'caixa' | 'banco'
}

/**
 * Conta de caixa ou banco, COM saldo — a tela de Contas financeiras (migração
 * 0023), não o select do formulário de lançamento (`ContaFinanceira` acima).
 *
 * `saldo_atual_centavos` é CACHE, mantido pelo servidor a partir das baixas
 * de contas a receber e a pagar apontadas para esta conta — o front nunca
 * calcula este número, só soma para o KPI de total (INV-11).
 */
export type ContaFinanceiraCompleta = {
  codigo: string
  nome: string
  tipo: 'caixa' | 'banco'
  ativa: boolean
  saldo_inicial_centavos: Centavos
  saldo_atual_centavos: Centavos
  /**
   * Falso depois do primeiro título baixado nesta conta. Vem PRONTO do
   * servidor: reescrever a premissa de um saldo já fechado é decisão que o
   * PUT recusa com 409, e a tela lê este campo em vez de re-derivar quando.
   */
  saldo_inicial_editavel: boolean
}

export type ListaDeContasFinanceiras = {
  contas: ContaFinanceiraCompleta[]
}

/** O corpo de POST /financeiro/contas. */
export type PedidoDeNovaContaFinanceira = {
  codigo: string
  nome: string
  tipo: 'caixa' | 'banco'
  saldo_inicial_centavos: Centavos
}

/**
 * O corpo de PUT /financeiro/contas/{codigo}.
 *
 * Sem `tipo`: trocar caixa por banco depois que a conta já tem título
 * apontando para ela reclassificaria histórico em silêncio.
 */
export type PedidoDeEdicaoContaFinanceira = {
  nome: string
  ativa: boolean
  saldo_inicial_centavos: Centavos
}

// ------------------------------------------------------------------ listagem

export type LinhaConta = {
  /** `R0001` / `P0001`. A identidade da rota. */
  codigo: string
  /** `REC-000123` / `PAG-000123`. O número que o lojista escolhe e imprime. */
  numero_documento: string
  descricao: string
  /** `aaaa-mm-dd`. A data do documento — não é o vencimento nem o `criado_em`. */
  emissao: string
  /** Nulo nos títulos antigos, gerados por venda antes da categoria existir. */
  categoria?: CategoriaFinanceira
  /** Só no A receber. */
  cliente?: PessoaResumo
  /** Só no A pagar. */
  fornecedor?: PessoaResumo
  /** Código da venda que gerou a parcela (`V0001`), quando veio do PDV. */
  venda?: string
  /** Número da nota fiscal de compra que gerou a conta, quando veio dela. */
  nota_fiscal?: string
  numero_parcela: number
  total_parcelas: number

  valor_bruto_centavos: Centavos
  desconto_centavos: Centavos
  acrescimo_centavos: Centavos
  /** O LÍQUIDO — é o que vale. `bruto − desconto + acréscimo`. */
  valor_centavos: Centavos

  /** `aaaa-mm-dd`. Data pura, sem hora e sem fuso. */
  vencimento: string
  forma?: FormaConta
  forma_rotulo?: string
  /** A conta PREVISTA. Ausente é "definir na baixa". */
  conta_financeira?: ContaFinanceira
  observacao?: string

  status: StatusConta
  /** O status já derivado pelo servidor — inclui `atrasada`. */
  situacao: SituacaoConta
  situacao_rotulo: string
  /** `aaaa-mm-dd` da baixa. Ausente enquanto a conta está aberta. */
  baixada_em?: string
  /**
   * O que de fato entrou ou saiu. Pode diferir de `valor_centavos` por juro de
   * atraso ou desconto de pontualidade — e é este valor que o fluxo de caixa
   * soma, não o previsto.
   */
  valor_baixado_centavos?: Centavos

  /**
   * Se este título aceita edição e exclusão, e por que não aceita.
   *
   * Vem PRONTO do servidor, e o front não recalcula. A regra tem duas partes
   * ("veio de documento" e "já produziu efeito"), e mantê-la nos dois lados
   * seria mantê-la em um só depois da primeira mudança — o sintoma é um botão
   * habilitado que falha depois do clique. O servidor recusa com 409 de
   * qualquer forma; o que estes três campos evitam é a tela oferecer.
   */
  editavel: boolean
  excluivel: boolean
  /** A razão escrita em pt-BR. Vazia quando não há bloqueio. */
  motivo_bloqueio?: string
}

/**
 * Os números dos KPIs e dos chips.
 *
 * Vêm do servidor IGNORANDO o chip e o recorte escolhidos — só o período e a
 * busca contam. É isso que faz o chip não clicado continuar mostrando quantas
 * contas existem nele, em vez de zerar a cada clique.
 *
 * `aberta` conta só o que está em aberto e AINDA NO PRAZO: atrasada é recorte
 * próprio, e somar os dois dá o total em aberto. Contar a atrasada nos dois
 * faria a soma dos chips passar do total e ninguém entenderia por quê.
 */
export type ContagensDeContas = {
  total: number
  aberta: number
  atrasada: number
  /** `recebida` no A receber, `paga` no A pagar. */
  baixada: number
  cancelada: number

  vencem_hoje: number
  vencem_na_semana: number
  baixadas_no_mes: number

  atrasado_centavos: Centavos
  vence_hoje_centavos: Centavos
  vence_na_semana_centavos: Centavos
  baixado_no_mes_centavos: Centavos
}

/** O pé da tabela: soma o RECORTE inteiro, não a página. Canceladas fora. */
export type SomasDeContas = {
  total_centavos: Centavos
  /** Em aberto e no prazo. */
  aberto_centavos: Centavos
  atrasado_centavos: Centavos
  /** Já recebido ou já pago, pelo valor que de fato entrou/saiu. */
  baixado_centavos: Centavos
}

export type ListaDeContas = {
  linhas: LinhaConta[]
  total: number
  pagina: number
  tamanho: number
  paginas: number
  contagens: ContagensDeContas
  somas: SomasDeContas
}

export type FiltroDeContas = {
  /** `2026-09-01` — data local, inclusiva, comparada com o vencimento. */
  de?: string
  /** `2026-10-01` — data local, EXCLUSIVA (o servidor compara com `<`). */
  ate?: string
  /** Código do cliente (receber) ou do fornecedor (pagar). */
  pessoa?: string
  situacao?: SituacaoConta | ''
  recorte?: RecorteDeContas | ''
  busca?: string
  pagina?: number
  tamanho?: number
}

// -------------------------------------------------------------------- resumo

/**
 * Os KPIs de `GET /financeiro/resumo` — tudo em centavos.
 *
 * As contagens são opcionais porque são apoio ao número, não o número: uma
 * resposta sem elas continua desenhando o valor, que é o que decide se alguém
 * corre atrás de cobrança hoje.
 */
export type ResumoFinanceiro = {
  a_receber_hoje_centavos: Centavos
  a_receber_atrasado_centavos: Centavos
  a_pagar_hoje_centavos: Centavos
  a_pagar_atrasado_centavos: Centavos
  recebido_no_mes_centavos: Centavos
  pago_no_mes_centavos: Centavos
  /** Recebido − pago no mês, mais o que ainda está previsto até o fim dele. */
  saldo_projetado_centavos: Centavos

  contas_a_receber_atrasadas?: number
  contas_a_pagar_atrasadas?: number
}

// ------------------------------------------------------------------- pedidos

/**
 * O lançamento manual, no formato exato de `POST /financeiro/{lado}`.
 *
 * `pessoa` vira `cliente` no receber e `fornecedor` no pagar — a assimetria do
 * contrato, resolvida em `lancarConta` para não vazar para a tela.
 */
export type PedidoDeLancamento = {
  numero_documento: string
  pessoa?: string
  categoria: string
  descricao: string
  valor_bruto_centavos: Centavos
  desconto_centavos: Centavos
  acrescimo_centavos: Centavos
  /** `aaaa-mm-dd`. */
  emissao: string
  /** `aaaa-mm-dd` do vencimento da primeira parcela. */
  vencimento: string
  /** Com mais de uma, o servidor divide o LÍQUIDO por `dinheiro.Ratear`. */
  parcelas: number
  forma: FormaConta
  conta_financeira?: string
  /**
   * Nasce já baixado, com a data de hoje e o valor líquido. É a ÚNICA situação
   * em que criar um título mexe em saldo — e por isso a tela diz isso em texto,
   * não só no rótulo. Recusado junto com parcelamento.
   */
  ja_liquidada: boolean
  observacao?: string
}

/** Uma linha por parcela gerada, mais o que o rateio produziu. */
export type ResultadoDeLancamento = {
  contas?: LinhaConta[]
  /** Quantas parcelas nasceram. */
  parcelas?: number
  /** A soma delas — que fecha com o líquido, por `dinheiro.Ratear`. */
  total_centavos?: Centavos
}

export type PedidoDeBaixa = {
  /**
   * O que de fato entrou ou saiu, que pode ser diferente do previsto — juro de
   * atraso para mais, desconto de pontualidade para menos.
   */
  valor_centavos: Centavos
  /** `aaaa-mm-dd`. */
  data: string
}

// ------------------------------------------------------------------ chamadas

export function carregarResumoFinanceiro(sinal?: AbortSignal): Promise<ResumoFinanceiro> {
  return api.get<ResumoFinanceiro>('/financeiro/resumo', sinal)
}

/**
 * A listagem de um dos lados. O recorte é do SERVIDOR, sempre.
 *
 * Filtrar em memória daria certo hoje e mentiria no dia em que a loja tiver
 * mais contas do que cabe na primeira página: o pé diria "soma do recorte"
 * somando só o que coube na tela.
 */
export function carregarContas(
  lado: Lado,
  filtro: FiltroDeContas = {},
  sinal?: AbortSignal,
): Promise<ListaDeContas> {
  const params = new URLSearchParams()
  if (filtro.de) params.set('de', filtro.de)
  if (filtro.ate) params.set('ate', filtro.ate)
  // O nome do parâmetro muda com o lado — é a única assimetria do contrato.
  if (filtro.pessoa) params.set(lado === 'receber' ? 'cliente' : 'fornecedor', filtro.pessoa)
  if (filtro.situacao) params.set('situacao', filtro.situacao)
  if (filtro.recorte) params.set('recorte', filtro.recorte)
  if (filtro.busca) params.set('busca', filtro.busca)
  if (filtro.pagina && filtro.pagina > 1) params.set('pagina', String(filtro.pagina))
  if (filtro.tamanho) params.set('tamanho', String(filtro.tamanho))

  const consulta = params.toString()
  return api.get<ListaDeContas>(`/financeiro/${lado}${consulta ? `?${consulta}` : ''}`, sinal)
}

/**
 * As categorias do tipo escolhido. O filtro por tipo é o que cumpre a promessa
 * do formulário: "só categorias de receita aparecem aqui".
 */
export function carregarCategorias(
  tipo: TipoLancamento,
  sinal?: AbortSignal,
): Promise<{ categorias: CategoriaFinanceira[] }> {
  return api.get<{ categorias: CategoriaFinanceira[] }>(
    `/financeiro/categorias?tipo=${tipo}`,
    sinal,
  )
}

/** Caixa e bancos da loja, para o campo "Conta prevista". */
export function carregarContasFinanceiras(
  sinal?: AbortSignal,
): Promise<{ contas: ContaFinanceira[] }> {
  return api.get<{ contas: ContaFinanceira[] }>('/financeiro/contas-financeiras', sinal)
}

/**
 * TODAS as contas financeiras da loja (ativas e inativas), com saldo — a
 * tela de Contas financeiras. Rota distinta de `carregarContasFinanceiras`
 * (que só serve o select do lançamento, sem saldo).
 */
export function carregarContasFinanceirasCompletas(
  sinal?: AbortSignal,
): Promise<ListaDeContasFinanceiras> {
  return api.get<ListaDeContasFinanceiras>('/financeiro/contas', sinal)
}

/** Cadastra uma conta financeira nova, com saldo inicial. */
export function criarContaFinanceira(
  pedido: PedidoDeNovaContaFinanceira,
): Promise<ContaFinanceiraCompleta> {
  return api.post<ContaFinanceiraCompleta>('/financeiro/contas', pedido)
}

/**
 * Edita nome, situação e — se ainda for permitido — o saldo inicial.
 *
 * Reenviar o mesmo saldo inicial que já está gravado nunca é recusado, mesmo
 * numa conta com movimento: o servidor só trava quando o VALOR muda de
 * verdade (`financeiro.Servico.EditarContaFinanceira`).
 */
export function editarContaFinanceira(
  codigo: string,
  pedido: PedidoDeEdicaoContaFinanceira,
): Promise<ContaFinanceiraCompleta> {
  return api.put<ContaFinanceiraCompleta>(
    `/financeiro/contas/${encodeURIComponent(codigo)}`,
    pedido,
  )
}

/** O `REC-000123` sugerido. Editável na tela, e único por loja e por lado. */
export function carregarProximoDocumento(
  lado: Lado,
  sinal?: AbortSignal,
): Promise<{ numero_documento: string }> {
  return api.get<{ numero_documento: string }>(`/financeiro/${lado}/proximo-documento`, sinal)
}

function corpoDoLancamento(lado: Lado, pedido: PedidoDeLancamento): Record<string, unknown> {
  const corpo: Record<string, unknown> = {
    numero_documento: pedido.numero_documento,
    categoria: pedido.categoria,
    descricao: pedido.descricao,
    valor_bruto_centavos: pedido.valor_bruto_centavos,
    desconto_centavos: pedido.desconto_centavos,
    acrescimo_centavos: pedido.acrescimo_centavos,
    emissao: pedido.emissao,
    vencimento: pedido.vencimento,
    parcelas: pedido.parcelas,
    forma: pedido.forma,
    ja_liquidada: pedido.ja_liquidada,
  }
  if (pedido.pessoa) corpo[lado === 'receber' ? 'cliente' : 'fornecedor'] = pedido.pessoa
  if (pedido.conta_financeira) corpo.conta_financeira = pedido.conta_financeira
  if (pedido.observacao) corpo.observacao = pedido.observacao
  return corpo
}

/** Lançamento manual. Uma linha por parcela; nada é gravado pela metade. */
export function lancarConta(
  lado: Lado,
  pedido: PedidoDeLancamento,
): Promise<ResultadoDeLancamento> {
  return api.post<ResultadoDeLancamento>(`/financeiro/${lado}`, corpoDoLancamento(lado, pedido))
}

/**
 * Edita o lançamento manual em aberto.
 *
 * Título vindo de documento volta 409 dizendo por quê — e a tela mostra o texto
 * da API, porque quem escreveu a regra sabe explicá-la melhor que um genérico.
 */
export function salvarConta(
  lado: Lado,
  codigo: string,
  pedido: PedidoDeLancamento,
): Promise<LinhaConta> {
  return api.put<LinhaConta>(
    `/financeiro/${lado}/${encodeURIComponent(codigo)}`,
    corpoDoLancamento(lado, pedido),
  )
}

/**
 * Exclui o lançamento manual em aberto. Mesmas recusas da edição.
 *
 * Responde 200 com corpo, não 204 — e o corpo é ignorado de propósito: o que a
 * tela faz depois é recarregar a listagem, e a linha excluída não volta nela.
 */
export function excluirConta(lado: Lado, codigo: string): Promise<unknown> {
  return api.delete<unknown>(`/financeiro/${lado}/${encodeURIComponent(codigo)}`)
}

/**
 * Marca a conta como recebida/paga.
 *
 * Baixa em conta já baixada volta 409 — e a tela mostra o texto que a API
 * manda, em vez de inventar "erro ao salvar": quem deu a baixa antes precisa
 * saber que já estava feita, não que falhou.
 */
export function baixarConta(
  lado: Lado,
  codigo: string,
  pedido: PedidoDeBaixa,
): Promise<LinhaConta> {
  return api.post<LinhaConta>(
    `/financeiro/${lado}/${encodeURIComponent(codigo)}/baixa`,
    pedido,
  )
}

// ------------------------------------------------------------------ derivados

/** O status baixado de cada lado. */
export function statusBaixado(lado: Lado): StatusConta {
  return lado === 'receber' ? 'recebida' : 'paga'
}

/** Hoje em `aaaa-mm-dd`, no fuso do navegador — o mesmo dia que o lojista vê. */
export function hojeISO(agora: Date = new Date()): string {
  const ano = agora.getFullYear()
  const mes = String(agora.getMonth() + 1).padStart(2, '0')
  const dia = String(agora.getDate()).padStart(2, '0')
  return `${ano}-${mes}-${dia}`
}

/**
 * Bruto − desconto + acréscimo.
 *
 * Soma e subtração de inteiros, o que §1 permite ao front — o que ela proíbe é
 * DIVIDIR fora de `Ratear`. É esta conta que o formulário mostra em "Valor
 * líquido", que é exibido e nunca digitado, e é a mesma que o banco garante
 * por `check` na coluna `valor_centavos`.
 */
export function liquidoDe(
  bruto: Centavos,
  desconto: Centavos,
  acrescimo: Centavos,
): Centavos {
  return bruto - desconto + acrescimo
}

/**
 * O quanto a linha pede ação HOJE. É isto, e não o status gravado, que decide
 * a cor da linha (spec 01: cor só onde alguém precisa agir).
 */
export type Urgencia = 'atrasada' | 'vence_hoje' | 'a_vencer' | 'quitada' | 'cancelada'

/**
 * Deriva a urgência do vencimento, não de um rótulo gravado.
 *
 * A comparação é de texto porque `aaaa-mm-dd` ordena lexicograficamente igual
 * a como ordena no calendário — e `new Date('2026-09-10')` seria meia-noite em
 * UTC, ou seja, dia 9 no Brasil. Já custou um dia de diferença em tela.
 */
export function urgenciaDaConta(linha: LinhaConta, hoje: string = hojeISO()): Urgencia {
  if (linha.status === 'cancelada') return 'cancelada'
  if (linha.status === 'recebida' || linha.status === 'paga') return 'quitada'
  if (linha.vencimento < hoje) return 'atrasada'
  if (linha.vencimento === hoje) return 'vence_hoje'
  return 'a_vencer'
}

/** Quantos dias a conta está vencida. Zero quando ainda está no prazo. */
export function diasDeAtraso(linha: LinhaConta, hoje: string = hojeISO()): number {
  if (urgenciaDaConta(linha, hoje) !== 'atrasada') return 0
  const dia = 24 * 60 * 60 * 1000
  const vencimento = Date.parse(`${linha.vencimento}T00:00:00`)
  const referencia = Date.parse(`${hoje}T00:00:00`)
  return Math.max(Math.round((referencia - vencimento) / dia), 0)
}

/** `2020-08-28` → `28/08/2020`. Data pura, sem passar por `Date` e sem fuso. */
function porExtenso(iso: string): string {
  return iso.split('-').reverse().join('/')
}

/**
 * O que a situação significa em palavras — a linha de apoio embaixo do selo.
 *
 * Mora aqui, e não na tela, porque a mesma frase é escrita em dois lugares —
 * na tabela e na gaveta. Duas cópias divergiriam na primeira mudança, e a
 * pessoa leria explicações diferentes para o mesmo estado.
 */
export function apoioDaSituacao(linha: LinhaConta, lado: Lado): string {
  const urgencia = urgenciaDaConta(linha)

  if (urgencia === 'cancelada') return 'fora das somas'
  if (urgencia === 'quitada') {
    if (linha.baixada_em) return `em ${porExtenso(linha.baixada_em)}`
    return lado === 'receber' ? 'o dinheiro entrou' : 'o dinheiro saiu'
  }
  if (urgencia === 'atrasada') {
    const dias = diasDeAtraso(linha)
    return `${dias} ${dias === 1 ? 'dia' : 'dias'} de atraso`
  }
  if (urgencia === 'vence_hoje') return 'é o trabalho de hoje'
  return 'no prazo'
}

/** De onde o título nasceu. Decide se ele pode ser editado ou excluído. */
export function origemDaConta(linha: LinhaConta): 'venda' | 'nota_fiscal' | 'manual' {
  if (linha.venda) return 'venda'
  if (linha.nota_fiscal) return 'nota_fiscal'
  return 'manual'
}

/**
 * A razão de o título não aceitar edição nem exclusão — ou `undefined` quando
 * aceita.
 *
 * NÃO recalcula nada: só lê `editavel`, `excluivel` e `motivo_bloqueio`, que o
 * servidor manda prontos. A regra tem duas partes ("veio de documento" e "já
 * produziu efeito"), e mantê-la também aqui seria mantê-la em um só lugar
 * depois da primeira mudança — o sintoma é a tela oferecer um botão que o
 * servidor recusa com 409 depois do clique.
 *
 * Existe como função, e não como leitura direta do campo, porque as duas telas
 * precisam do mesmo fallback: um `motivo_bloqueio` vazio num título bloqueado
 * deixaria o item desabilitado sem explicação nenhuma.
 */
export function bloqueioDeEdicao(linha: LinhaConta): string | undefined {
  if (linha.editavel) return undefined
  return linha.motivo_bloqueio || 'este título não aceita edição'
}

export function bloqueioDeExclusao(linha: LinhaConta): string | undefined {
  if (linha.excluivel) return undefined
  return linha.motivo_bloqueio || 'este título não aceita exclusão'
}

// --------------------------------------------------------------- formulário

/** O estado do formulário de lançamento, do jeito que a tela o segura. */
export type RascunhoDeLancamento = {
  tipo: TipoLancamento
  numero_documento: string
  /** Código do cliente ou do fornecedor. */
  pessoa: string
  categoria: string
  descricao: string
  /** `null` quando o texto digitado ainda não é um valor. */
  valor_bruto_centavos: Centavos | null
  emissao: string
  vencimento: string
  forma: FormaConta | ''
}

/**
 * O que ainda falta para salvar, PELO NOME, na ordem em que a tela pergunta.
 *
 * O rodapé lista os nomes em vez de só desabilitar o botão porque "Lançar
 * título" apagado e sem explicação faz a pessoa procurar o campo errado num
 * formulário de seis grupos. Botão desabilitado com a razão escrita vale mais
 * que botão que falha depois do clique.
 */
export function faltaParaSalvar(rascunho: RascunhoDeLancamento): string[] {
  const falta: string[] = []
  if (!rascunho.numero_documento.trim()) falta.push('número do documento')
  if (!rascunho.pessoa.trim()) falta.push(rascunho.tipo === 'receita' ? 'cliente' : 'fornecedor')
  if (!rascunho.categoria) falta.push('categoria')
  if (!rascunho.descricao.trim()) falta.push('descrição')
  if (rascunho.valor_bruto_centavos === null || rascunho.valor_bruto_centavos <= 0) {
    falta.push('valor bruto maior que zero')
  }
  if (!rascunho.emissao) falta.push('emissão')
  if (!rascunho.vencimento) falta.push('vencimento')
  if (!rascunho.forma) falta.push('forma de pagamento')
  return falta
}
